import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Card,
  InputAdornment,
  Chip,
  Collapse,
  LinearProgress,
  Fade,
  Skeleton,
  Grid,
} from "@mui/material";
import axios from "axios";

// Icons
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import WhatsAppRoundedIcon from "@mui/icons-material/WhatsApp";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { AutoTaskAuthContext } from "../Context/AutoTaskContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════════

const PLACEHOLDER_TAGS = [
  { tag: "<DATE>", description: "Current date" },
  { tag: "<TIME>", description: "Current time" },
  { tag: "<STORENAME>", description: "Store name" },
  { tag: "<VEHTYPE>", description: "Vehicle type" },
  { tag: "<VEHNO>", description: "Vehicle number" },
  { tag: "<TRIPDETAILS>", description: "Trip details" },
  { tag: "<BOOKINGAMT>", description: "Booking amount" },
  { tag: "<DRIVERNAME>", description: "Driver name" },
  { tag: "<DRIVERCONTACT>", description: "Driver contact" },
  { tag: "<CUSTNAME>", description: "Customer name" },
  { tag: "<CUSTMOBNO>", description: "Customer mobile" },
  { tag: "<PICKUPADDESS>", description: "Pickup address" },
  { tag: "<PICKUPLOCATION>", description: "Pickup location" },
  { tag: "<PAYNOTE>", description: "Payment note" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#FFFFFF",
    borderRadius: "12px",
    color: "#111827",
    fontSize: 14.5,
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#9CA3AF", borderWidth: "1.5px" },
    "&.Mui-focused fieldset": { borderColor: "#4F46E5", borderWidth: "2px" },
    "&.Mui-error fieldset": { borderColor: "#EF4444" },
  },
  "& .MuiInputLabel-root": {
    color: "#6B7280",
    fontSize: 13.5,
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#4F46E5", fontWeight: 600 },
  "& .MuiInputLabel-root.Mui-error": { color: "#EF4444" },
  "& .MuiSvgIcon-root": { color: "#9CA3AF" },
  "& .MuiFormHelperText-root": {
    color: "#EF4444",
    fontSize: 11.5,
    mt: 0.75,
    ml: 0.5,
  },
};

const EMPTY_FORM = {
  whatsapp_template: [],
  whatsapp_token: "",
  bank_name: "",
  account_holder_name: "",
  account_number: "",
  ifsc_code: "",
  branch_name: "",
  upi_id: "",
  invoice_prefix: "",
  invoice_footer_note: "",
  // company_logo holds either:
  //   ""         → no logo
  //   "data:..."  → fresh base64 (not yet saved on server)
  //   buffer obj → converted to base64
  company_logo: "",
};

// ══════════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS (unchanged from original)
// ══════════════════════════════════════════════════════════════════════════════

const SectionHeader = ({
  icon: Icon,
  title,
  subtitle,
  color,
  bgColor,
  borderColor,
  action,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 3,
      pb: 2,
      borderBottom: `2px solid ${borderColor || "#E5E7EB"}`,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          bgcolor: bgColor,
          border: `2px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 2px 8px ${color}20`,
        }}
      >
        <Icon sx={{ fontSize: 22, color }} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 19,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: -0.3,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: "#6B7280", mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {action}
  </Box>
);

const InfoBadge = ({ icon: Icon, label, value, color, bgColor }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      bgcolor: bgColor,
      border: `1.5px solid ${color}30`,
      borderRadius: "10px",
      px: 1.75,
      py: 1,
      flex: 1,
      minWidth: 180,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        bgcolor: color + "15",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 18, color }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontSize: 11,
          color: "#6B7280",
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14.5,
          fontWeight: 700,
          color: "#111827",
          fontFamily: "'DM Mono', monospace",
        }}
      >
        {value || "Not Set"}
      </Typography>
    </Box>
  </Box>
);

const ValidationMessage = ({ type, message }) => {
  const config = {
    error: {
      icon: ErrorRoundedIcon,
      color: "#EF4444",
      bg: "#FEF2F2",
      border: "#FCA5A5",
    },
    warning: {
      icon: WarningAmberRoundedIcon,
      color: "#F59E0B",
      bg: "#FFFBEB",
      border: "#FCD34D",
    },
    success: {
      icon: CheckCircleRoundedIcon,
      color: "#10B981",
      bg: "#F0FDF4",
      border: "#86EFAC",
    },
    info: {
      icon: InfoOutlinedIcon,
      color: "#3B82F6",
      bg: "#EFF6FF",
      border: "#93C5FD",
    },
  };
  const cfg = config[type] || config.info;
  const Icon = cfg.icon;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        bgcolor: cfg.bg,
        border: `1.5px solid ${cfg.border}`,
        borderRadius: "10px",
        p: 1.5,
      }}
    >
      <Icon sx={{ fontSize: 18, color: cfg.color, mt: 0.15, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: 13,
          color: cfg.color,
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TEMPLATE DIALOG
// ══════════════════════════════════════════════════════════════════════════════

const TemplateDialog = ({ open, onClose, editData, onSave }) => {
  const isEdit = !!editData;
  const [form, setForm] = useState({ name: "", content: "" });
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setShowPreview(false);
    setForm(
      isEdit
        ? { name: editData.name || "", content: editData.content || "" }
        : { name: "", content: "" },
    );
  }, [open, editData]);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Template name is required";
    if (form.name && form.name.length > 10)
      e.name = "Template name must be 10 characters or less";
    if (!form.content?.trim()) e.content = "Template content is required";
    if (form.content && form.content.length < 20)
      e.content = "Template content must be at least 20 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form, isEdit);
    onClose();
  };
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const insertTag = (tag) => {
    const textarea = document.querySelector(
      'textarea[name="template-content"]',
    );
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent =
        form.content.substring(0, start) + tag + form.content.substring(end);
      setForm((p) => ({ ...p, content: newContent }));
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    }
  };

  const getPreviewContent = () => {
    let preview = form.content;
    PLACEHOLDER_TAGS.forEach(({ tag, description }) => {
      preview = preview.replaceAll(tag, `[${description}]`);
    });
    return preview;
  };

  const charCount = form.content?.length || 0;
  const charLimit = 1000;
  const charPercentage = (charCount / charLimit) * 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#FFFFFF",
          border: "1.5px solid #E5E7EB",
          borderRadius: "20px",
          backgroundImage: "none",
          boxShadow: "0 12px 48px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#DCFCE7",
              border: "1.5px solid #86EFAC",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WhatsAppRoundedIcon sx={{ fontSize: 20, color: "#16A34A" }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: 17, color: "#111827" }}
            >
              {isEdit ? "Edit WhatsApp Template" : "Create WhatsApp Template"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.25 }}>
              {isEdit
                ? "Update your message template"
                : "Add a new message template"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#9CA3AF",
            "&:hover": { bgcolor: "#F3F4F6", color: "#111827" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "#E5E7EB" }} />

      <Box
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <TextField
          label="Template Name"
          size="small"
          fullWidth
          value={form.name}
          onChange={set("name")}
          error={!!errors.name}
          helperText={errors.name || "Short identifier (e.g., BC, DD, BD)"}
          placeholder="e.g., BC"
          inputProps={{ maxLength: 10 }}
          sx={fieldSx}
        />

        <Box>
          <TextField
            label="Template Content"
            name="template-content"
            size="small"
            fullWidth
            multiline
            minRows={8}
            maxRows={12}
            value={form.content}
            onChange={set("content")}
            error={!!errors.content}
            helperText={errors.content}
            placeholder="Enter your WhatsApp message template."
            inputProps={{ maxLength: charLimit }}
            sx={fieldSx}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1,
              px: 0.5,
            }}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(charPercentage, 100)}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: "#F3F4F6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor:
                      charPercentage > 90
                        ? "#EF4444"
                        : charPercentage > 70
                        ? "#F59E0B"
                        : "#10B981",
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: charPercentage > 90 ? "#EF4444" : "#6B7280",
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {charCount} / {charLimit}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}
            >
              Available Placeholders
            </Typography>
            <Tooltip title="Click to insert at cursor position" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              bgcolor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              p: 1.5,
              maxHeight: 140,
              overflowY: "auto",
            }}
          >
            {PLACEHOLDER_TAGS.map(({ tag, description }) => (
              <Tooltip key={tag} title={description} arrow placement="top">
                <Chip
                  label={tag}
                  size="small"
                  onClick={() => insertTag(tag)}
                  sx={{
                    bgcolor: "#EEF2FF",
                    color: "#4F46E5",
                    fontSize: 11.5,
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "1px solid #C7D2FE",
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: "#C7D2FE",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 6px rgba(79, 70, 229, 0.2)",
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Box>
          <Button
            onClick={() => setShowPreview(!showPreview)}
            startIcon={
              showPreview ? (
                <VisibilityOffRoundedIcon />
              ) : (
                <PreviewRoundedIcon />
              )
            }
            size="small"
            fullWidth
            sx={{
              color: "#6B7280",
              borderColor: "#E5E7EB",
              borderRadius: "10px",
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              border: "1.5px solid",
              "&:hover": {
                borderColor: "#4F46E5",
                bgcolor: "#EEF2FF",
                color: "#4F46E5",
              },
            }}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <Collapse in={showPreview}>
            <Box
              sx={{
                mt: 2,
                bgcolor: "#F9FAFB",
                border: "1.5px solid #E5E7EB",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#6B7280",
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <PreviewRoundedIcon sx={{ fontSize: 14 }} /> Preview (with
                sample data)
              </Typography>
              <Box
                sx={{
                  bgcolor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  p: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#111827",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {getPreviewContent() || "No content to preview"}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#E5E7EB" }} />
      <Box sx={{ px: 3, py: 2.5, display: "flex", gap: 1.5 }}>
        <Button
          onClick={onClose}
          fullWidth
          sx={{
            bgcolor: "#F9FAFB",
            color: "#6B7280",
            borderRadius: "10px",
            py: 1.1,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            border: "1.5px solid #E5E7EB",
            "&:hover": { bgcolor: "#F3F4F6", borderColor: "#9CA3AF" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          fullWidth
          sx={{
            bgcolor: "#16A34A",
            color: "#FFFFFF",
            borderRadius: "10px",
            py: 1.1,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 2px 12px #16A34A30",
            "&:hover": {
              bgcolor: "#15803D",
              boxShadow: "0 4px 16px #16A34A40",
            },
          }}
        >
          {isEdit ? "Update Template" : "Create Template"}
        </Button>
      </Box>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TEMPLATE CARD
// ══════════════════════════════════════════════════════════════════════════════

const TemplateCard = ({ template, index, onEdit, onDelete }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(template.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const lines = template.content.split("\n");
  const previewLines = lines.slice(0, 3).join("\n");
  const hasMore = lines.length > 3;

  return (
    <Card
      sx={{
        bgcolor: "#FFFFFF",
        border: "1.5px solid #E5E7EB",
        borderRadius: "14px",
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#16A34A",
          boxShadow: "0 4px 16px rgba(22, 163, 74, 0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "linear-gradient(135deg, #DCFCE7 0%, #F0FDF4 100%)",
          borderBottom: "1.5px solid #86EFAC",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: "#FFFFFF",
              border: "1.5px solid #86EFAC",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WhatsAppRoundedIcon sx={{ fontSize: 18, color: "#16A34A" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#111827",
                fontFamily: "'DM Mono', monospace",
                letterSpacing: 0.5,
              }}
            >
              {template.name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
              {template.content.length} characters
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`#${index + 1}`}
          size="small"
          sx={{
            bgcolor: "#16A34A",
            color: "#FFFFFF",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "'DM Mono', monospace",
            height: 22,
          }}
        />
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: "10px",
            p: 1.5,
            mb: 1.5,
            maxHeight: expanded ? "none" : 100,
            overflowY: "auto",
            transition: "all 0.3s ease",
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "#374151",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            {expanded ? template.content : previewLines}
          </Typography>
        </Box>
        {hasMore && (
          <Button
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{
              color: "#16A34A",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              mb: 1,
              "&:hover": { bgcolor: "#F0FDF4" },
            }}
          >
            {expanded ? "Show Less" : "Show More"}
          </Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={copied ? "Copied!" : "Copy content"} arrow>
            <Button
              onClick={handleCopy}
              size="small"
              startIcon={
                copied ? (
                  <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                flex: 1,
                bgcolor: copied ? "#F0FDF4" : "#F9FAFB",
                color: copied ? "#16A34A" : "#6B7280",
                border: `1.5px solid ${copied ? "#86EFAC" : "#E5E7EB"}`,
                borderRadius: "8px",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  bgcolor: copied ? "#DCFCE7" : "#F3F4F6",
                  borderColor: copied ? "#16A34A" : "#9CA3AF",
                },
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </Tooltip>
          <Tooltip title="Edit template" arrow>
            <IconButton
              onClick={onEdit}
              size="small"
              sx={{
                bgcolor: "#EEF2FF",
                border: "1.5px solid #C7D2FE",
                color: "#4F46E5",
                "&:hover": { bgcolor: "#C7D2FE" },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete template" arrow>
            <IconButton
              onClick={onDelete}
              size="small"
              sx={{
                bgcolor: "#FEF2F2",
                border: "1.5px solid #FCA5A5",
                color: "#DC2626",
                "&:hover": { bgcolor: "#FEE2E2" },
              }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

const CompanySettings = ({ storeID }) => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const userStoreID = storeID || userInfo?.store_id;

  // ── State ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editTemplateIndex, setEditTemplateIndex] = useState(-1);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [showToken, setShowToken] = useState(false);

  const showSnack = (msg, sev = "success") =>
    setSnack({ open: true, msg, sev });

  // ── Image helper: File → base64 ───────────────────────────────────────────
  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ── Helper: Process logo from backend (handles URLs, base64, or buffers) ──
  const processLogoFromBackend = (logoValue) => {
    if (!logoValue) return "";

    try {
      // If it's already a valid URL (http/https) or base64 (data:), return as-is
      if (typeof logoValue === "string") {
        if (
          logoValue.startsWith("http://") ||
          logoValue.startsWith("https://") ||
          logoValue.startsWith("data:")
        ) {
          return logoValue;
        }

        // Try to parse as JSON (in case it's a stringified buffer)
        try {
          const parsed = JSON.parse(logoValue);

          // Check if it's a buffer object
          if (
            parsed &&
            parsed.type === "Buffer" &&
            Array.isArray(parsed.data)
          ) {
            // Convert buffer array to base64
            const bytes = new Uint8Array(parsed.data);
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64 = btoa(binary);
            return `data:image/png;base64,${base64}`;
          }
        } catch (e) {
          // Not JSON, just return the string as-is
          return logoValue;
        }
      }

      return logoValue;
    } catch (e) {
      console.error("Error processing logo:", e);
      return "";
    }
  };

  // ── Fetch settings on mount / when storeID changes ──────────────────────
  const fetchSettings = useCallback(async () => {
    if (userStoreID === undefined || userStoreID === null) return;

    try {
      setFetchLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/ticket/store-settings/${userStoreID}`,
      );

      if (!res.data.success) throw res.data;

      const d = res.data.data;

      if (d) {
        // Process the logo (handles URL, base64, or buffer)
        let logoValue = d.company_logo || "";
        if (logoValue) {
          logoValue = processLogoFromBackend(logoValue);
        }

        setForm({
          whatsapp_template: Array.isArray(d.whatsapp_template)
            ? d.whatsapp_template
            : [],
          whatsapp_token: d.whatsapp_token || "",
          bank_name: d.bank_name || "",
          account_holder_name: d.account_holder_name || "",
          account_number: d.account_number || "",
          ifsc_code: d.ifsc_code || "",
          branch_name: d.branch_name || "",
          upi_id: d.upi_id || "",
          invoice_prefix: d.invoice_prefix || "",
          invoice_footer_note: d.invoice_footer_note || "",
          company_logo: logoValue,
        });
      } else {
        setForm(EMPTY_FORM);
      }

      setHasUnsavedChanges(false);
    } catch (error) {
      showSnack(error?.statusMessage || "Failed to load settings", "error");
    } finally {
      setFetchLoading(false);
    }
  }, [userStoreID]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // ── Generic field setter ─────────────────────────────────────────────────
  const set = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setHasUnsavedChanges(true);
    if (errors[key]) setErrors((p) => ({ ...p, [key]: null }));
  };

  // ── Logo upload ──────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      showSnack("Please upload PNG or JPG image only", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showSnack("Image size must be less than 2MB", "error");
      return;
    }

    const base64 = await convertFileToBase64(file);
    setForm((p) => ({ ...p, company_logo: base64 }));
    setHasUnsavedChanges(true);
    showSnack("Logo ready to save ✓", "success");
  };

  const handleRemoveLogo = () => {
    setForm((p) => ({ ...p, company_logo: "" }));
    setHasUnsavedChanges(true);
    showSnack("Logo removed", "info");
  };

  // ── Template handlers ────────────────────────────────────────────────────
  const handleAddTemplate = () => {
    setEditTemplate(null);
    setEditTemplateIndex(-1);
    setTemplateDialog(true);
  };
  const handleEditTemplate = (t, i) => {
    setEditTemplate(t);
    setEditTemplateIndex(i);
    setTemplateDialog(true);
  };
  const handleDeleteTemplate = (i) => {
    setForm((p) => ({
      ...p,
      whatsapp_template: p.whatsapp_template.filter((_, idx) => idx !== i),
    }));
    setHasUnsavedChanges(true);
    showSnack("Template deleted", "info");
  };
  const handleSaveTemplate = (data, isEdit) => {
    if (isEdit) {
      const updated = [...form.whatsapp_template];
      updated[editTemplateIndex] = data;
      setForm((p) => ({ ...p, whatsapp_template: updated }));
      showSnack("Template updated ✓", "success");
    } else {
      setForm((p) => ({
        ...p,
        whatsapp_template: [...p.whatsapp_template, data],
      }));
      showSnack("Template added ✓", "success");
    }
    setHasUnsavedChanges(true);
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validateAll = () => {
    const e = {};
    if (form.whatsapp_token && form.whatsapp_token.length < 20)
      e.whatsapp_token = "Token must be at least 20 characters";
    if (form.account_number && !/^\d+$/.test(form.account_number))
      e.account_number = "Account number must contain only digits";
    if (form.account_number && form.account_number.length < 9)
      e.account_number = "Account number must be at least 9 digits";
    if (form.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code))
      e.ifsc_code = "Invalid IFSC code format (e.g., SBIN0001234)";
    if (form.upi_id && !/@/.test(form.upi_id))
      e.upi_id = "UPI ID must contain @ symbol";
    if (form.invoice_prefix && form.invoice_prefix.length > 10)
      e.invoice_prefix = "Prefix must be 10 characters or less";
    if (form.invoice_prefix && !/^[A-Z0-9-]+$/.test(form.invoice_prefix))
      e.invoice_prefix =
        "Prefix can only contain uppercase letters, numbers, and hyphens";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save settings ────────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      const payload = {
        store_id: userStoreID,
        whatsapp_template: JSON.stringify(form.whatsapp_template),
        whatsapp_token: form.whatsapp_token || null,
        bank_name: form.bank_name || null,
        account_holder_name: form.account_holder_name || null,
        account_number: form.account_number || null,
        ifsc_code: form.ifsc_code || null,
        branch_name: form.branch_name || null,
        upi_id: form.upi_id || null,
        invoice_prefix: form.invoice_prefix || null,
        invoice_footer_note: form.invoice_footer_note || null,
        company_logo: form.company_logo || null,
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/ticket/store-settings`,
        payload,
      );

      if (!res.data.success) throw res.data;

      showSnack(
        res.data.statusMessage || "Settings saved successfully ✓",
        "success",
      );
      setHasUnsavedChanges(false);

      // Refresh to get the updated buffer/logo
      fetchSettings();
    } catch (error) {
      showSnack(error?.statusMessage || "Failed to save settings", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    fetchSettings();
    showSnack("Reloaded from server", "info");
  };

  // ── Progress helpers ─────────────────────────────────────────────────────
  const whatsappCompletion =
    (form.whatsapp_token ? 33 : 0) +
    (form.whatsapp_template.length > 0 ? 34 : 0);
  const bankCompletion =
    (form.bank_name ? 16.66 : 0) +
    (form.account_holder_name ? 16.66 : 0) +
    (form.account_number ? 16.66 : 0) +
    (form.ifsc_code ? 16.66 : 0) +
    (form.branch_name ? 16.66 : 0) +
    (form.upi_id ? 16.7 : 0);
  const invoiceCompletion =
    (form.invoice_prefix ? 33 : 0) +
    (form.invoice_footer_note ? 33 : 0) +
    (form.company_logo ? 34 : 0);

  const tabsData = [
    {
      label: "WhatsApp",
      icon: WhatsAppRoundedIcon,
      color: "#16A34A",
      completion: whatsappCompletion,
    },
    {
      label: "Banking",
      icon: AccountBalanceRoundedIcon,
      color: "#4F46E5",
      completion: bankCompletion,
    },
    {
      label: "Invoice",
      icon: ReceiptLongRoundedIcon,
      color: "#D97706",
      completion: invoiceCompletion,
    },
  ];

  // ── Logo display (handles Azure URLs, base64, or any valid image source) ─
  const logoDisplaySrc = form.company_logo || null;

  // Check if it's a fresh upload (base64) vs saved to server (URL)
  const isNewUpload = logoDisplaySrc && logoDisplaySrc.startsWith("data:");

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#F9FAFB",
        fontFamily: "'DM Sans', sans-serif",
        color: "#111827",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&family=DM+Mono:wght@400;500;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #F3F4F6; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #9CA3AF; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #6B7280; }
      `}</style>

      {/* ═══ HEADER ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#FFFFFF",
          borderBottom: "2px solid #E5E7EB",
          px: { xs: 2, md: 4 },
          py: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(79, 70, 229, 0.3)",
              }}
            >
              <SettingsRoundedIcon sx={{ fontSize: 26, color: "#FFFFFF" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 800,
                  letterSpacing: -0.8,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Company Settings
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
                Configure your business settings and preferences
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            {fetchLoading && (
              <Chip
                label="Loading…"
                size="small"
                sx={{
                  bgcolor: "#EEF2FF",
                  color: "#4F46E5",
                  border: "1.5px solid #C7D2FE",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            )}
            {hasUnsavedChanges && !fetchLoading && (
              <Chip
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
                label="Unsaved Changes"
                size="small"
                sx={{
                  bgcolor: "#FFFBEB",
                  color: "#D97706",
                  border: "1.5px solid #FCD34D",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            )}
            <Button
              onClick={handleReset}
              startIcon={<RefreshRoundedIcon />}
              size="small"
              disabled={fetchLoading}
              sx={{
                bgcolor: "#F9FAFB",
                color: "#6B7280",
                borderRadius: "10px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                px: 2,
                border: "1.5px solid #E5E7EB",
                "&:hover": { bgcolor: "#F3F4F6", borderColor: "#9CA3AF" },
              }}
            >
              Reload
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={loading || !hasUnsavedChanges || fetchLoading}
              startIcon={
                loading ? (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      border: "2px solid #FFFFFF",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              sx={{
                bgcolor: "#4F46E5",
                color: "#FFFFFF",
                borderRadius: "10px",
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                px: 3,
                py: 1,
                boxShadow: "0 2px 12px #4F46E530",
                "&:hover": {
                  bgcolor: "#4338CA",
                  boxShadow: "0 4px 16px #4F46E540",
                },
                "&:disabled": {
                  bgcolor: "#E5E7EB",
                  color: "#9CA3AF",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? "Saving…" : "Save Settings"}
            </Button>
          </Box>
        </Box>

        {/* Tab progress bars */}
        <Box sx={{ display: "flex", gap: 2, mt: 2.5, flexWrap: "wrap" }}>
          {tabsData.map((tab, idx) => {
            const Icon = tab.icon;
            return (
              <Box
                key={idx}
                onClick={() => setActiveTab(idx)}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  bgcolor: activeTab === idx ? tab.color + "10" : "#F9FAFB",
                  border: `1.5px solid ${
                    activeTab === idx ? tab.color + "50" : "#E5E7EB"
                  }`,
                  borderRadius: "12px",
                  p: 1.75,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: tab.color,
                    bgcolor: tab.color + "10",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18, color: tab.color }} />
                    <Typography
                      sx={{ fontSize: 13.5, fontWeight: 700, color: "#111827" }}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: tab.color,
                      fontFamily: "'DM Mono', monospace",
                    }}
                  >
                    {Math.round(tab.completion)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={tab.completion}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: tab.color,
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ═══ MAIN CONTENT ═════════════════════════════════════════════════ */}
      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {/* Loading skeleton */}
          {fetchLoading && (
            <Card
              sx={{
                bgcolor: "#FFFFFF",
                border: "1.5px solid #E5E7EB",
                borderRadius: "16px",
                p: 4,
              }}
            >
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: "10px", mb: 2 }}
                />
              ))}
            </Card>
          )}

          {/* ── TAB 0: WhatsApp ─────────────────────────────────────────── */}
          {!fetchLoading && activeTab === 0 && (
            <Fade in timeout={400}>
              <Box>
                <Card
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1.5px solid #E5E7EB",
                    borderRadius: "16px",
                    p: 4,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <SectionHeader
                    icon={WhatsAppRoundedIcon}
                    title="WhatsApp Configuration"
                    subtitle="Configure WhatsApp Business API and message templates"
                    color="#16A34A"
                    bgColor="#DCFCE7"
                    borderColor="#86EFAC"
                  />

                  <Box
                    sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}
                  >
                    <InfoBadge
                      icon={KeyRoundedIcon}
                      label="API Status"
                      value={
                        form.whatsapp_token ? "Connected" : "Not Connected"
                      }
                      color={form.whatsapp_token ? "#16A34A" : "#EF4444"}
                      bgColor={form.whatsapp_token ? "#F0FDF4" : "#FEF2F2"}
                    />
                    <InfoBadge
                      icon={WhatsAppRoundedIcon}
                      label="Templates"
                      value={`${form.whatsapp_template.length} Active`}
                      color="#16A34A"
                      bgColor="#F0FDF4"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      mb: 4,
                    }}
                  >
                    <TextField
                      label="WhatsApp API Token"
                      size="small"
                      fullWidth
                      type={showToken ? "text" : "password"}
                      value={form.whatsapp_token}
                      onChange={set("whatsapp_token")}
                      error={!!errors.whatsapp_token}
                      helperText={
                        errors.whatsapp_token ||
                        "Your WhatsApp Business API authentication token"
                      }
                      placeholder="Enter your WhatsApp API token"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyRoundedIcon
                              sx={{ fontSize: 18, color: "#16A34A" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowToken(!showToken)}
                              edge="end"
                            >
                              {showToken ? (
                                <VisibilityOffRoundedIcon
                                  sx={{ fontSize: 18 }}
                                />
                              ) : (
                                <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  </Box>

                  <Divider sx={{ my: 4, borderColor: "#E5E7EB" }} />

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: "#111827",
                            mb: 0.5,
                          }}
                        >
                          Message Templates
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                          Create reusable message templates with dynamic
                          placeholders
                        </Typography>
                      </Box>
                      <Button
                        onClick={handleAddTemplate}
                        startIcon={<AddRoundedIcon />}
                        sx={{
                          bgcolor: "#16A34A",
                          color: "#FFFFFF",
                          borderRadius: "10px",
                          fontSize: 14,
                          fontWeight: 600,
                          textTransform: "none",
                          px: 2.5,
                          boxShadow: "0 2px 12px #16A34A30",
                          "&:hover": { bgcolor: "#15803D" },
                        }}
                      >
                        Create Template
                      </Button>
                    </Box>

                    {form.whatsapp_template.length === 0 ? (
                      <Box
                        sx={{
                          bgcolor: "#F9FAFB",
                          border: "2px dashed #D1D5DB",
                          borderRadius: "14px",
                          py: 8,
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "16px",
                            bgcolor: "#DCFCE7",
                            border: "2px solid #86EFAC",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <WhatsAppRoundedIcon
                            sx={{ fontSize: 32, color: "#16A34A" }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#374151",
                            mb: 1,
                          }}
                        >
                          No Templates Created Yet
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, color: "#9CA3AF", mb: 3 }}
                        >
                          Start by creating your first WhatsApp message template
                        </Typography>
                        <Button
                          onClick={handleAddTemplate}
                          startIcon={<AddRoundedIcon />}
                          sx={{
                            bgcolor: "#16A34A",
                            color: "#FFFFFF",
                            borderRadius: "10px",
                            fontSize: 13,
                            fontWeight: 600,
                            textTransform: "none",
                            px: 3,
                          }}
                        >
                          Create Your First Template
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)",
                          },
                          gap: 2.5,
                        }}
                      >
                        {form.whatsapp_template.map((t, i) => (
                          <TemplateCard
                            key={i}
                            template={t}
                            index={i}
                            onEdit={() => handleEditTemplate(t, i)}
                            onDelete={() => handleDeleteTemplate(i)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Card>
              </Box>
            </Fade>
          )}

          {/* ── TAB 1: Banking ──────────────────────────────────────────── */}
          {!fetchLoading && activeTab === 1 && (
            <Fade in timeout={400}>
              <Box>
                <Card
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1.5px solid #E5E7EB",
                    borderRadius: "16px",
                    p: 4,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <SectionHeader
                    icon={AccountBalanceRoundedIcon}
                    title="Banking Information"
                    subtitle="Manage your bank account and payment details"
                    color="#4F46E5"
                    bgColor="#EEF2FF"
                    borderColor="#C7D2FE"
                  />

                  <Box
                    sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}
                  >
                    <InfoBadge
                      icon={AccountBalanceRoundedIcon}
                      label="Bank Account"
                      value={form.account_number ? "Configured" : "Not Set"}
                      color={form.account_number ? "#16A34A" : "#EF4444"}
                      bgColor={form.account_number ? "#F0FDF4" : "#FEF2F2"}
                    />
                    <InfoBadge
                      icon={AccountBalanceWalletRoundedIcon}
                      label="UPI Payment"
                      value={form.upi_id ? "Active" : "Not Set"}
                      color={form.upi_id ? "#16A34A" : "#EF4444"}
                      bgColor={form.upi_id ? "#F0FDF4" : "#FEF2F2"}
                    />
                    <InfoBadge
                      icon={SecurityRoundedIcon}
                      label="Verification"
                      value={form.ifsc_code ? "Verified" : "Pending"}
                      color={form.ifsc_code ? "#16A34A" : "#F59E0B"}
                      bgColor={form.ifsc_code ? "#F0FDF4" : "#FFFBEB"}
                    />
                  </Box>

                  <ValidationMessage
                    type="info"
                    message="Bank details are used for payment settlements and invoice generation. Ensure all information is accurate."
                  />

                  <Box sx={{ mt: 4 }}>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#111827",
                        mb: 3,
                      }}
                    >
                      Account Information
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Bank Name"
                          size="small"
                          fullWidth
                          value={form.bank_name}
                          onChange={set("bank_name")}
                          placeholder="e.g., State Bank of India"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountBalanceRoundedIcon
                                  sx={{ fontSize: 18, color: "#4F46E5" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account Holder Name"
                          size="small"
                          fullWidth
                          value={form.account_holder_name}
                          onChange={set("account_holder_name")}
                          placeholder="Enter account holder name"
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account Number"
                          size="small"
                          fullWidth
                          value={form.account_number}
                          onChange={set("account_number")}
                          error={!!errors.account_number}
                          helperText={errors.account_number}
                          placeholder="Enter account number"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="IFSC Code"
                          size="small"
                          fullWidth
                          value={form.ifsc_code}
                          onChange={set("ifsc_code")}
                          error={!!errors.ifsc_code}
                          helperText={errors.ifsc_code || "e.g., SBIN0001234"}
                          placeholder="Enter IFSC code"
                          inputProps={{ style: { textTransform: "uppercase" } }}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Branch Name"
                          size="small"
                          fullWidth
                          value={form.branch_name}
                          onChange={set("branch_name")}
                          placeholder="Enter branch name"
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="UPI ID"
                          size="small"
                          fullWidth
                          value={form.upi_id}
                          onChange={set("upi_id")}
                          error={!!errors.upi_id}
                          helperText={errors.upi_id || "e.g., merchant@paytm"}
                          placeholder="Enter UPI ID"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    bgcolor: "#F0FDF4",
                                    border: "1px solid #86EFAC",
                                    borderRadius: "6px",
                                    px: 0.75,
                                    py: 0.3,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "#16A34A",
                                      fontWeight: 700,
                                      fontFamily: "'DM Mono', monospace",
                                    }}
                                  >
                                    UPI
                                  </Typography>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              </Box>
            </Fade>
          )}

          {/* ── TAB 2: Invoice ──────────────────────────────────────────── */}
          {!fetchLoading && activeTab === 2 && (
            <Fade in timeout={400}>
              <Box>
                <Card
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1.5px solid #E5E7EB",
                    borderRadius: "16px",
                    p: 4,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <SectionHeader
                    icon={ReceiptLongRoundedIcon}
                    title="Invoice Configuration"
                    subtitle="Customize your invoice appearance and settings"
                    color="#D97706"
                    bgColor="#FFFBEB"
                    borderColor="#FCD34D"
                  />

                  <Box
                    sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}
                  >
                    <InfoBadge
                      icon={DescriptionRoundedIcon}
                      label="Invoice Prefix"
                      value={form.invoice_prefix || "Not Set"}
                      color="#D97706"
                      bgColor="#FFFBEB"
                    />
                    <InfoBadge
                      icon={ImageRoundedIcon}
                      label="Company Logo"
                      value={form.company_logo ? "Uploaded" : "Not Set"}
                      color={form.company_logo ? "#16A34A" : "#EF4444"}
                      bgColor={form.company_logo ? "#F0FDF4" : "#FEF2F2"}
                    />
                    <InfoBadge
                      icon={TuneRoundedIcon}
                      label="Customization"
                      value={
                        form.invoice_prefix && form.company_logo
                          ? "Complete"
                          : "Partial"
                      }
                      color={
                        form.invoice_prefix && form.company_logo
                          ? "#16A34A"
                          : "#F59E0B"
                      }
                      bgColor={
                        form.invoice_prefix && form.company_logo
                          ? "#F0FDF4"
                          : "#FFFBEB"
                      }
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {/* Left: Prefix + Footer */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#111827",
                              mb: 2,
                            }}
                          >
                            Invoice Numbering
                          </Typography>
                          <TextField
                            label="Invoice Prefix"
                            size="small"
                            fullWidth
                            value={form.invoice_prefix}
                            onChange={set("invoice_prefix")}
                            error={!!errors.invoice_prefix}
                            helperText={
                              errors.invoice_prefix ||
                              "Prefix for invoice numbers (uppercase letters, numbers, hyphens)"
                            }
                            placeholder="e.g., INV"
                            inputProps={{
                              maxLength: 10,
                              style: { textTransform: "uppercase" },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#6B7280",
                                      fontFamily: "'DM Mono', monospace",
                                      bgcolor: "#F3F4F6",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: "6px",
                                    }}
                                  >
                                    -0001
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            sx={fieldSx}
                          />
                          {form.invoice_prefix && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "#F0FDF4",
                                border: "1.5px solid #86EFAC",
                                borderRadius: "10px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#16A34A",
                                  mb: 0.75,
                                }}
                              >
                                Preview
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#111827",
                                  fontFamily: "'DM Mono', monospace",
                                }}
                              >
                                {form.invoice_prefix.toUpperCase()}-0001
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#111827",
                              mb: 2,
                            }}
                          >
                            Footer Note
                          </Typography>
                          <TextField
                            label="Invoice Footer Note"
                            size="small"
                            fullWidth
                            multiline
                            minRows={4}
                            value={form.invoice_footer_note}
                            onChange={set("invoice_footer_note")}
                            placeholder="e.g., Thank you for your business!"
                            sx={fieldSx}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* Right: Logo upload */}
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            mb: 2,
                          }}
                        >
                          Company Logo
                        </Typography>

                        {logoDisplaySrc ? (
                          <Box>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: 280,
                                borderRadius: "14px",
                                border: "2px solid #E5E7EB",
                                overflow: "hidden",
                                bgcolor: "#F9FAFB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <img
                                src={logoDisplaySrc}
                                alt="Company Logo"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                              {/* Show badge only for new uploads not yet saved */}
                              {isNewUpload && (
                                <Chip
                                  label="Not saved yet"
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "#FFFBEB",
                                    color: "#D97706",
                                    border: "1.5px solid #FCD34D",
                                    fontWeight: 600,
                                    fontSize: 11,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              <input
                                accept="image/png,image/jpeg,image/jpg"
                                style={{ display: "none" }}
                                id="logo-upload-change"
                                type="file"
                                onChange={handleFileUpload}
                              />
                              <label
                                htmlFor="logo-upload-change"
                                style={{ flex: 1 }}
                              >
                                <Button
                                  component="span"
                                  fullWidth
                                  startIcon={<CloudUploadRoundedIcon />}
                                  sx={{
                                    bgcolor: "#F9FAFB",
                                    color: "#374151",
                                    border: "1.5px solid #E5E7EB",
                                    borderRadius: "10px",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    py: 1.1,
                                    "&:hover": {
                                      bgcolor: "#F3F4F6",
                                      borderColor: "#9CA3AF",
                                    },
                                  }}
                                >
                                  Change Logo
                                </Button>
                              </label>
                              <Button
                                onClick={handleRemoveLogo}
                                startIcon={<DeleteSweepRoundedIcon />}
                                sx={{
                                  bgcolor: "#FEF2F2",
                                  color: "#DC2626",
                                  border: "1.5px solid #FCA5A5",
                                  borderRadius: "10px",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textTransform: "none",
                                  px: 2.5,
                                  py: 1.1,
                                  "&:hover": {
                                    bgcolor: "#FEE2E2",
                                    borderColor: "#DC2626",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              bgcolor: "#F9FAFB",
                              border: "2px dashed #D1D5DB",
                              borderRadius: "14px",
                              py: 6,
                              textAlign: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "16px",
                                bgcolor: "#FFFBEB",
                                border: "2px solid #FCD34D",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 2,
                              }}
                            >
                              <ImageRoundedIcon
                                sx={{ fontSize: 32, color: "#D97706" }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#374151",
                                mb: 0.5,
                              }}
                            >
                              Upload Company Logo
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12, color: "#9CA3AF", mb: 3 }}
                            >
                              PNG or JPG, max 2MB
                              <br />
                              Recommended: 200x200px
                            </Typography>
                            <input
                              accept="image/png,image/jpeg,image/jpg"
                              style={{ display: "none" }}
                              id="logo-upload"
                              type="file"
                              onChange={handleFileUpload}
                            />
                            <label htmlFor="logo-upload">
                              <Button
                                component="span"
                                startIcon={<CloudUploadRoundedIcon />}
                                sx={{
                                  bgcolor: "#D97706",
                                  color: "#FFFFFF",
                                  borderRadius: "10px",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textTransform: "none",
                                  px: 3,
                                  boxShadow: "0 2px 12px #D9770630",
                                  "&:hover": { bgcolor: "#B45309" },
                                }}
                              >
                                Choose File
                              </Button>
                            </label>
                          </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                          <ValidationMessage
                            type="info"
                            message="Your logo will appear on all generated invoices and documents. Choose a high-quality image for best results."
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>

      {/* ═══ DIALOGS & SNACKBAR ═══════════════════════════════════════════ */}
      <TemplateDialog
        open={templateDialog}
        onClose={() => {
          setTemplateDialog(false);
          setEditTemplate(null);
          setEditTemplateIndex(-1);
        }}
        editData={editTemplate}
        onSave={handleSaveTemplate}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          variant="filled"
          icon={
            snack.sev === "success" ? (
              <CheckCircleRoundedIcon />
            ) : snack.sev === "error" ? (
              <ErrorRoundedIcon />
            ) : (
              <InfoOutlinedIcon />
            )
          }
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: 13.5,
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanySettings;
